import { Autocomplete, Grid, MenuItem, Select, SelectChangeEvent, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { RelationType } from '@thro-types/access-handler';

import { isAfter, isToday } from 'date-fns';
import { parseAsInteger, useQueryState } from 'nuqs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEntitlementGroupsQuery } from 'api/hooks/accessHandler';
import { translations } from 'locales/i18n';
import { formatToISODateWithoutTime } from 'utils/dates';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { isLeihBarAdmin } from '../helper';

export const DailyTimePicker = () => {
  const { t } = useTranslation();

  const [startDay, setStartDay] = useQueryState('startday', {
    defaultValue: formatToISODateWithoutTime(new Date()),
    shallow: false
  });
  const [endDay, setEndDay] = useQueryState('endday', {
    defaultValue: formatToISODateWithoutTime(new Date()),
    shallow: false
  });
  const [lenderGroupId, setLenderGroupId] = useQueryState('lenderGroupId', parseAsInteger);

  const isTodayCondition = isToday(new Date(startDay)) && isToday(new Date(endDay));

  const [daySelectValue, setDaySelectValue] = useState<'select' | 'today'>(
    isTodayCondition ? 'today' : 'select'
  );
  const [showDatePicker, setShowDatePicker] = useState(isTodayCondition ? false : true);

  // const [lenderGroupIdState, setLenderGroupIdState] = useState<number>();

  const { permissions } = useAuthContext();
  const { data: entitlementGroups } = useEntitlementGroupsQuery(isLeihBarAdmin(permissions), {
    params: { relationType: RelationType.LEIHBAR }
  });

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    if (event.target.value === 'today') {
      setDaySelectValue('today');
      setShowDatePicker(false);
      setStartDay(formatToISODateWithoutTime(new Date()));
      setEndDay(formatToISODateWithoutTime(new Date()));
    } else {
      setShowDatePicker(true);
      setDaySelectValue('select');
    }
  };

  const handleStartDayChange = (value: string | null) => {
    if (value) {
      if (isAfter(new Date(value), new Date(endDay))) {
        setEndDay(formatToISODateWithoutTime(new Date(value)));
      }
      setStartDay(formatToISODateWithoutTime(new Date(value)));
    }
  };

  const handleEndDayChange = (value: string | null) => {
    if (value) {
      setEndDay(formatToISODateWithoutTime(new Date(value)));
    }
  };

  return (
    <>
      <Select size="small" value={daySelectValue} onChange={handleSelectChange}>
        <MenuItem value={'today'}>{t(translations.pages.leihbar.daily.today())}</MenuItem>
        <MenuItem value={'select'}>{t(translations.pages.leihbar.daily.selectDay())}</MenuItem>
      </Select>
      {showDatePicker && (
        <>
          <DatePicker
            value={startDay}
            onChange={handleStartDayChange}
            renderInput={props => <TextField {...props} size="small" />}
            inputFormat="dd.MM.yyyy"
          />
          <DatePicker
            value={endDay}
            onChange={handleEndDayChange}
            renderInput={props => <TextField {...props} size="small" />}
            inputFormat="dd.MM.yyyy"
            minDate={startDay}
          />
        </>
      )}
      {(isLeihBarAdmin(permissions) ||
        (entitlementGroups && entitlementGroups?.data?.length > 1)) && (
        <Grid item xs={2}>
          <Autocomplete
            id="lenderGroupId"
            fullWidth
            size="small"
            options={entitlementGroups?.data ?? []}
            value={entitlementGroups?.data.find(ent => ent.id === lenderGroupId) || null}
            getOptionLabel={option => option.name}
            renderInput={params => (
              <TextField
                {...params}
                label={t(translations.pages.leihbar.daily.lenderGroup())}
                placeholder={t(translations.pages.leihbar.daily.lenderGroup())}
                size="small"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => {
              setLenderGroupId(value?.id ?? null);
            }}
          />
        </Grid>
      )}
    </>
  );
};
